import { Card } from "@exhousing/design-system";
import { Building2, ListTodo, Target, Wallet } from "lucide-react";

import { useCrmStats, useLeads } from "@/hooks/api/useCrm";

function euro(v: number): string {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v);
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Target;
  label: string;
  value: string;
  tone?: "warning";
}) {
  return (
    <Card className="flex items-center gap-3 p-4">
      <span
        className={`grid size-10 shrink-0 place-items-center rounded-lg ${
          tone === "warning" ? "bg-warning/15 text-warning" : "bg-muted text-muted-foreground"
        }`}
      >
        <Icon className="size-5" />
      </span>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="truncate text-xl font-medium tabular-nums">{value}</p>
      </div>
    </Card>
  );
}

/** At-a-glance CRM health: open pipeline value, open leads, accounts, open tasks. */
export function CrmStatsHeader() {
  const { data: stats } = useCrmStats();
  const { data: leads } = useLeads();

  const openLeads = (leads ?? []).filter((l) => !l.converted_landlord_id);
  const pipelineValue = openLeads.reduce((sum, l) => sum + (l.price_indication ?? 0), 0);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard icon={Wallet} label="Open pipeline" value={euro(pipelineValue)} />
      <StatCard icon={Target} label="Open leads" value={String(openLeads.length)} />
      <StatCard
        icon={Building2}
        label="Accounts"
        value={String(stats?.accounts_total ?? 0)}
      />
      <StatCard
        icon={ListTodo}
        label="Open tasks"
        value={String(stats?.open_tasks ?? 0)}
        tone={stats && stats.open_tasks > 0 ? "warning" : undefined}
      />
    </div>
  );
}
